import type {
  ImageLayout,
  PdfQuality,
  ViewerMode,
  ViewerState,
} from '@genesisframeworks/viewer-plugin';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getApiBase } from '@services/api/apiConfig';
import { presentationViewForViewerMode } from '@/utils/helpers/embedHelpers';
import { ViewerPanel } from '@/viewer/ViewerPanel';
import type { EmbedPresentationView, EmbedShare } from '@/types/embed.types';

const PRESENTATION_MODES: Record<EmbedPresentationView, ViewerMode> = {
  gallery: 'image-gallery',
  carousel: 'image-carousel',
  multiview: 'image-viewer',
  'pdf-library': 'pdf-library',
  'pdf-list': 'pdf-list',
  'pdf-reader': 'pdf-reader',
  'pdf-flipbook': 'pdf-flipbook',
};

function legacyPresentation(share: EmbedShare): EmbedPresentationView {
  return share.viewType;
}

function presentationFromSearch(share: EmbedShare, search: string): EmbedPresentationView {
  const value = new URLSearchParams(search).get('view') as EmbedPresentationView | null;
  return value && value in PRESENTATION_MODES ? value : legacyPresentation(share);
}

function parsePositiveNumber(value: string | null): number | undefined {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function parsePdfQuality(value: string | null): PdfQuality {
  return value && ['sharp', 'balanced', 'performance'].includes(value)
    ? value as PdfQuality
    : 'auto';
}

export function StandaloneEmbedShellPage() {
  const { shareId = '' } = useParams();
  const location = useLocation();
  const [share, setShare] = useState<EmbedShare | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!shareId) {
      setError('Missing embed share ID');
      return;
    }

    async function loadShare() {
      try {
        const response = await fetch(
          `${getApiBase()}/api/public/embed/${encodeURIComponent(shareId)}`,
          { credentials: 'omit' },
        );
        const body = await response.json().catch(() => null);
        if (!response.ok) throw new Error(body?.error || `HTTP ${response.status}`);
        if (active) setShare(body);
      } catch (loadError) {
        if (active) setError((loadError as Error)?.message || 'Unable to open share');
      }
    }

    void loadShare();
    return () => { active = false; };
  }, [shareId]);

  const presentation = useMemo(
    () => share ? presentationFromSearch(share, location.search) : 'gallery',
    [share, location.search],
  );
  const mode = PRESENTATION_MODES[presentation];
  const contentType: 'image' | 'pdf' = mode.startsWith('image-') ? 'image' : 'pdf';
  const initialState = useMemo<Partial<ViewerState>>(() => {
    const params = new URLSearchParams(location.search);
    const layout = params.get('layout');
    return {
      layout: layout && ['filmstrip', 'sidebar', 'slider', 'grid'].includes(layout)
        ? layout as ImageLayout
        : presentation === 'multiview' ? 'filmstrip' : undefined,
      album: params.get('album') || undefined,
      image: params.get('image') || undefined,
      book: params.get('book') || undefined,
      page: parsePositiveNumber(params.get('page')),
      pdfQuality: parsePdfQuality(params.get('quality')),
    };
  }, [presentation, location.search]);

  if (!share && !error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', backgroundColor: '#202124' }}>
        <Stack spacing={1.5} alignItems="center">
          <CircularProgress size={32} sx={{ color: 'rgba(255,255,255,0.65)' }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.65)' }}>Opening…</Typography>
        </Stack>
      </Box>
    );
  }

  if (error || !share) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', backgroundColor: '#202124', p: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 560, borderRadius: 1.5 }}>
          {error || 'Unable to open share'}
        </Alert>
      </Box>
    );
  }

  const syncUrlState = (state: ViewerState) => {
    const url = new URL(window.location.href);
    const nextPresentation = presentationViewForViewerMode(state.mode, contentType);
    url.searchParams.set('view', nextPresentation);
    for (const key of ['layout', 'album', 'image', 'book', 'page'] as const) {
      const value = state[key];
      if (key === 'layout' && nextPresentation !== 'multiview') url.searchParams.delete(key);
      else if (value) url.searchParams.set(key, String(value));
      else url.searchParams.delete(key);
    }
    if (state.pdfQuality && state.pdfQuality !== 'auto') {
      url.searchParams.set('quality', state.pdfQuality);
    } else {
      url.searchParams.delete('quality');
    }
    window.history.replaceState({}, '', url);
  };

  return (
    <Box sx={{ height: '100vh', minHeight: 0, backgroundColor: '#202124' }}>
      <ViewerPanel
        auth={{ kind: 'public', shareId }}
        title={share.title}
        contentType={contentType}
        initialMode={mode}
        initialState={initialState}
        onStateChange={syncUrlState}
      />
    </Box>
  );
}
